import { handleApiError, notifyError } from './error-handler'
import { logApiCall } from '../lib/api-logger'
import posthog from 'posthog-js'
import { buildTraceparent } from '@/lib/trace-context'

export { startNewTrace, getCurrentTraceId } from '@/lib/trace-context'

interface FetchOptions extends RequestInit {
  notifyOnError?: boolean
  errorMessage?: string
  expectFile?: boolean
  downloadFileName?: string
}

export async function fetchWithErrorHandling<T = any>(
  url: string,
  options?: FetchOptions,
): Promise<T | Blob> {
  const {
    notifyOnError = true,
    errorMessage = 'שגיאה בביצוע הפעולה',
    expectFile = false,
    downloadFileName,
    ...fetchOptions
  } = options || {}

  const startTime = Date.now()
  const method = fetchOptions.method || 'GET'

  // Inject W3C traceparent + PostHog session ID for backend correlation.
  // Headers are only added for same-origin requests to prevent leaking session
  // identifiers to third-party APIs.
  const { traceparent, traceId, spanId } = buildTraceparent()
  const posthogSessionId: string = (posthog as any).get_session_id?.() ?? ''
  const isInternalRequest =
    url.startsWith('/') || (typeof window !== 'undefined' && url.startsWith(window.location.origin))
  fetchOptions.headers = {
    ...((fetchOptions.headers as Record<string, string>) ?? {}),
    ...(isInternalRequest ? { traceparent } : {}),
    ...(isInternalRequest && posthogSessionId ? { 'x-posthog-session-id': posthogSessionId } : {}),
  }

  // Capture in PostHog so errors can be correlated by traceId + spanId.
  // Wrapped in try/catch so a PostHog failure never blocks or throws on the API call.
  try {
    posthog.capture('$api_request', {
      traceId,
      spanId,
      url,
      method: method.toUpperCase(),
    })
  } catch {
    // PostHog capture errors are non-fatal — silently ignore
  }

  let serverTraceId = traceId
  try {
    const response = await fetch(url, fetchOptions)

    // Read back the traceId echoed by proxy.ts — confirms server received the correct trace
    serverTraceId = response.headers.get('x-trace-id') ?? traceId

    // Log successful API call
    logApiCall(method, url, startTime, response, undefined, fetchOptions.headers as any)

    if (!response.ok) {
      let errorData
      try {
        errorData = await response.json()
      } catch {
        errorData = { status: response.status, statusText: response.statusText }
      }

      const error = {
        ...errorData,
        status: response.status,
        statusText: response.statusText,
      }

      // Log API error
      logApiCall(method, url, startTime, response, error, fetchOptions.headers as any)

      throw error
    }

    // בדיקה אם זה קובץ או אם ציינו במפורש שאנחנו מצפים לקובץ
    const contentType = response.headers.get('Content-Type')
    const isFile =
      expectFile ||
      contentType?.includes('application/pdf') ||
      contentType?.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
      contentType?.includes('application/octet-stream') ||
      contentType?.includes('application/download')

    if (isFile) {
      const blob = await response.blob()

      if (downloadFileName) {
        downloadBlob(blob, downloadFileName, response)
      }

      return blob as T
    }

    return await response.json() // Default to JSON
  } catch (error) {
    // Log API error for network/other errors
    logApiCall(method, url, startTime, undefined, error, fetchOptions.headers as any)

    const errorDetails = handleApiError(error, errorMessage, true, {
      traceId: serverTraceId,
      spanId,
      url,
    })

    if (notifyOnError) {
      notifyError(errorDetails)
    }

    throw errorDetails
  }
}

function downloadBlob(blob: Blob, defaultFileName: string, response: Response) {
  let fileName = defaultFileName
  const contentDisposition = response.headers.get('Content-Disposition')

  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]*)/)
    if (fileNameMatch && fileNameMatch[1]) {
      fileName = decodeURIComponent(fileNameMatch[1])
    } else {
      const fallbackMatch = contentDisposition.match(/filename="([^"]*)"/)
      if (fallbackMatch && fallbackMatch[1]) {
        fileName = decodeURIComponent(fallbackMatch[1])
      }
    }
  }
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  window.URL.revokeObjectURL(url)
}

// Specialized versions for common HTTP methods
export const getWithErrorHandling = <T = any>(url: string, options?: FetchOptions) =>
  fetchWithErrorHandling<T>(url, { method: 'GET', ...options })

export const postWithErrorHandling = <T = any>(url: string, data: any, options?: FetchOptions) =>
  fetchWithErrorHandling<T>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    ...options,
  })

export const downloadFileWithErrorHandling = async (
  url: string,
  fileName: string,
  options?: Omit<FetchOptions, 'expectFile' | 'downloadFileName'>,
) => {
  return getWithErrorHandling(url, {
    ...options,
    expectFile: true,
    downloadFileName: fileName,
  })
}
